import { useEffect, useState } from 'react'
import { AlertTriangle, RefreshCw } from 'lucide-react'
import { Modal } from '@/components/Modal'
import { Button } from '@/components/ui/button'

/**
 * Global listener for the `bokap:stale-version` window event dispatched by
 * lib/api.ts when a 409 response carries `code: 'STALE_VERSION'` - i.e. the
 * row a form was about to save has been changed by someone else since the
 * form loaded it.
 *
 * Shows a single modal that prompts the user to reload the page so they
 * see the latest values before re-applying their changes. The save itself
 * already failed (the mutation threw) so the form's existing error path
 * also fires - this dialog is the global "you didn't lose your work, but
 * someone moved the room around" hint, not a replacement for the form's
 * own error handling.
 */

interface Detail {
  message: string
}

export function StaleVersionToast() {
  const [detail, setDetail] = useState<Detail | null>(null)

  useEffect(() => {
    const handler = (e: Event) => {
      const ce = e as CustomEvent<{ path: string; body: unknown; message: string }>
      const msg =
        (ce.detail.body && typeof ce.detail.body === 'object' && 'error' in ce.detail.body
          ? String((ce.detail.body as { error: unknown }).error)
          : null) ||
        ce.detail.message ||
        'This record was changed by someone else after you loaded it.'
      setDetail({ message: msg })
    }
    window.addEventListener('bokap:stale-version', handler)
    return () => window.removeEventListener('bokap:stale-version', handler)
  }, [])

  if (!detail) return null

  return (
    <Modal
      open={!!detail}
      onOpenChange={(v) => !v && setDetail(null)}
      title="This record was just updated"
      className="sm:max-w-md"
      footer={
        <>
          <Button variant="secondary" onClick={() => setDetail(null)}>
            Stay here
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setDetail(null)
              if (typeof window !== 'undefined') window.location.reload()
            }}
          >
            <RefreshCw className="size-4" aria-hidden />
            Reload to see latest
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-sm">
        <div className="flex gap-3 rounded-md bg-destructive/5 p-3 text-destructive">
          <AlertTriangle className="size-4 shrink-0" aria-hidden />
          <span>{detail.message}</span>
        </div>
        <p className="text-muted-foreground">
          Someone else in your workspace saved changes to this record while you were editing.
          Your changes were not saved. Reload to see the latest values, then re-apply your
          changes on top.
        </p>
      </div>
    </Modal>
  )
}
